import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appPath } from '@/api/client';
import { TodayCallScope, useTodayPost } from '@/api/TodayContext';
import type { RecapResponse } from '@/api/hooks';
import { failureOf, said } from '@/api/errors';

function dateInZone(zone: string): string {
  const parts = new Intl.DateTimeFormat('en', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const get = (kind: string) => parts.find(p => p.type === kind)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
export function DayRecap(props: { department: string; label: string; date: string; zone: string; open: (path: string, department: string) => void }) {
  return <TodayCallScope department={props.department}><Recap {...props} /></TodayCallScope>;
}
function Recap({ department, label, date, zone, open }: { department: string; label: string; date: string; zone: string; open: (path: string, department: string) => void }) {
  const post = useTodayPost(), arrived = useRef(false);
  const today = dateInZone(zone), current = date === today;
  const q = useQuery<RecapResponse>({ queryKey: ['today-workspace-recap', department, date, zone], queryFn: () => post(appPath('/v1/today/recap'), current ? {} : { day: date }), retry: false });
  useEffect(() => {
    if (!current || arrived.current) return;
    arrived.current = true;
    // Read the recap before recording this sitting, as the existing day does.
    if (q.isSuccess) void post(appPath('/v1/today/arrive')).catch(() => { arrived.current = false; });
    else arrived.current = false;
  }, [current, q.isSuccess, post]);
  if (q.isError) return <p role="alert">{said(failureOf(q.error))}</p>;
  if (!q.data || q.data.band === 'plan') return null;
  return <section aria-label={`${label} recap`} className="space-y-2 border border-line p-3">
    <h3 className="font-semibold">{current ? 'Since you left' : 'What happened'} · {label}</h3>
    {q.data.lines.length ? <ul>{q.data.lines.map((line, index) => <li key={index}><button className="text-left underline min-h-11 min-w-11 px-2" onClick={() => open(line.deepLink, department)}>{line.text}</button></li>)}</ul> : <p>Nothing recorded here that needs you.</p>}
  </section>;
}
